import { useEffect, useState } from 'react';
import { MetaRow } from '@/ui';
import { StudioButton as Button } from './styles';
import type { Ring } from './contracts';
import { studio } from './service';
import { useStudioTask } from './useStudioTask';
import { Field, Note, Row } from './styles';
export function RingEditor({ onChoose }: { onChoose: (ring: Ring) => void }) {
  const [items, setItems] = useState<Ring[]>([]);
  const [selected, setSelected] = useState<Ring>();
  const [name, setName] = useState('');
  const [people, setPeople] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const task = useStudioTask();
  const { setError } = task;
  useEffect(() => {
    let active = true;
    studio.rings().then(
      (r) => {
        if (active) setItems(r.items);
      },
      () => {
        if (active) setError('Could not load rings. Close and reopen to retry.');
      },
    );
    return () => {
      active = false;
    };
  }, [setError]);
  function select(r?: Ring) {
    setSelected(r);
    setName(r?.name ?? '');
    setPeople(r?.people.map((p) => p.handle).join(', ') ?? '');
    setConfirmDelete(false);
  }
  const changed =
    !selected ||
    name !== selected.name ||
    people !== selected.people.map((p) => p.handle).join(', ');
  return (
    <fieldset
      disabled={task.busy}
      style={{ border: 0, padding: 0, margin: 0, minWidth: 0, display: 'grid', gap: 16 }}
    >
      <Note>
        Named groups of people. Each published post keeps its selected recipients, even if the group
        changes later.
      </Note>
      <Row>
        {items.map((r) => (
          <Button
            key={r.id}
            aria-pressed={selected?.id === r.id}
            aria-label={`${r.name}, ${r.people.length} ${r.people.length === 1 ? 'person' : 'people'}`}
            onClick={() => select(r)}
          >
            <MetaRow>
              {r.name}
              {String(r.people.length)}
            </MetaRow>
          </Button>
        ))}
        <Button onClick={() => select()}>New ring</Button>
      </Row>
      <Field>
        Ring name
        <input maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field>
        People (handles separated by commas)
        <textarea value={people} onChange={(e) => setPeople(e.target.value)} />
      </Field>
      {selected && (
        <Note>
          Recipients: {selected.people.map((p) => '@' + p.handle.replace(/^@/, '')).join(', ')}
        </Note>
      )}
      {task.error && <p role="alert">{task.error}</p>}
      <Row>
        <Button
          disabled={task.busy || !name.trim() || !people.trim()}
          onClick={() =>
            void task.run(
              () =>
                studio.saveRing(
                  name.trim(),
                  people
                    .split(/[,\s]+/)
                    .filter(Boolean)
                    .map((h) => h.replace(/^@/, '')),
                  selected,
                ),
              (r) => {
                setItems([...items.filter((v) => v.id !== r.id), r]);
                select(r);
              },
            )
          }
        >
          Save ring
        </Button>
        <Button
          variant="primary"
          disabled={task.busy || changed || !selected?.people.length}
          onClick={() => selected && onChoose(selected)}
        >
          Use reviewed recipients
        </Button>
        {selected && (
          <Button variant="ghost" disabled={task.busy} onClick={() => setConfirmDelete(true)}>
            Delete ring
          </Button>
        )}
      </Row>
      {confirmDelete && selected && (
        <Row>
          <Note>Delete this group? Existing post recipients stay unchanged.</Note>
          <Button
            disabled={task.busy}
            onClick={() =>
              void task.run(
                () => studio.deleteRing(selected),
                () => {
                  setItems(items.filter((r) => r.id !== selected.id));
                  select();
                },
              )
            }
          >
            Confirm delete
          </Button>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
        </Row>
      )}
    </fieldset>
  );
}
